import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  getAllProviderConfigs, 
  createProviderConfigDocument, 
  updateProviderConfigDocument, 
  deleteProviderConfigDocument,
  initializePredefinedProviders,
  testProviderConnection,
  toggleProviderStatus,
  getProviderStatisticsSummary
} from '../../utils/providerConfigUtils';
import { 
  providerConfigModel, 
  formatProviderConfigForDisplay,
  predefinedProviders,
  getProviderStatistics
} from '../../data/providerConfigModel';

const ProviderConfigManager = () => {
  const { currentUser } = useAuth();
  const [providerConfigs, setProviderConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [testingProviderId, setTestingProviderId] = useState(null);
  const [initializing, setInitializing] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    status: 'all',
    propertyType: 'all',
    searchTerm: ''
  });
  
  const [filteredProviders, setFilteredProviders] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    providerName: '',
    providerCode: '',
    displayName: '',
    displayNameEn: '',
    apiConfig: {
      baseUrl: '',
      apiKey: '',
      secretKey: '',
      timeout: 30000,
      rateLimit: {
        requestsPerMinute: 60,
        requestsPerHour: 1000
      }
    },
    settings: {
      isActive: true,
      priority: 1,
      defaultLanguage: 'th',
      defaultCurrency: 'THB',
      supportedCountries: ['TH'],
      supportedPropertyTypes: ['hotels']
    },
    commissionConfig: {
      enabled: true,
      rates: {
        customer: 0.05,
        momo: 0.08,
        girl: 0.03,
        admin: 0.10
      },
      minimumCommission: 10,
      maximumCommission: 10000
    },
    features: {
      search: true,
      booking: true,
      cancellation: true,
      reviews: true,
      images: true,
      availability: true,
      pricing: true
    },
    status: 'active'
  });

  useEffect(() => {
    fetchProviderConfigs();
    fetchStatistics();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [providerConfigs, filters]);

  const fetchProviderConfigs = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await getAllProviderConfigs();
      if (result.success) {
        setProviderConfigs(result.data);
      } else {
        throw new Error(result.error || 'Failed to fetch provider configurations');
      }
    } catch (error) {
      console.error('Error fetching provider configs:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const result = await getProviderStatisticsSummary();
      if (result.success) {
        setStatistics(result.data);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...providerConfigs];
    
    // Status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter(provider => provider.status === filters.status);
    }
    
    // Property type filter
    if (filters.propertyType !== 'all') {
      filtered = filtered.filter(provider => 
        provider.settings.supportedPropertyTypes.includes(filters.propertyType)
      );
    }
    
    // Search term filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(provider => 
        provider.providerName.toLowerCase().includes(searchLower) ||
        provider.displayName.toLowerCase().includes(searchLower) ||
        provider.providerCode.toLowerCase().includes(searchLower)
      );
    }
    
    setFilteredProviders(filtered);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      status: 'all',
      propertyType: 'all',
      searchTerm: ''
    });
  };

  // Initialize predefined providers
  const handleInitializeProviders = async () => {
    if (!currentUser?.uid) return;
    
    setInitializing(true);
    try {
      const result = await initializePredefinedProviders(currentUser.uid);
      if (result.success) {
        alert(`Successfully initialized ${result.created} provider configurations`);
        fetchProviderConfigs();
        fetchStatistics();
      } else {
        alert(`Error initializing providers: ${result.error}`);
      }
    } catch (error) {
      console.error('Error initializing providers:', error);
      alert('Error initializing providers');
    } finally {
      setInitializing(false);
    }
  };

  // Create new provider configuration
  const handleCreateProvider = async (e) => {
    e.preventDefault();
    if (!currentUser?.uid) return;

    try {
      const result = await createProviderConfigDocument(formData, currentUser.uid);
      if (result.success) {
        alert('Provider configuration created successfully');
        setShowCreateModal(false);
        resetForm();
        fetchProviderConfigs();
        fetchStatistics();
      } else {
        alert(`Error creating provider: ${result.error}`);
      }
    } catch (error) {
      console.error('Error creating provider:', error);
      alert('Error creating provider');
    }
  };

  // Update provider configuration
  const handleUpdateProvider = async (e) => {
    e.preventDefault();
    if (!currentUser?.uid || !selectedProvider) return;

    try {
      const result = await updateProviderConfigDocument(selectedProvider.id, formData, currentUser.uid);
      if (result.success) {
        alert('Provider configuration updated successfully');
        setShowEditModal(false);
        setSelectedProvider(null);
        resetForm();
        fetchProviderConfigs();
        fetchStatistics();
      } else {
        alert(`Error updating provider: ${result.error}`);
      }
    } catch (error) {
      console.error('Error updating provider:', error);
      alert('Error updating provider');
    }
  };

  // Delete provider configuration
  const handleDeleteProvider = async (providerId) => {
    if (!window.confirm('Are you sure you want to delete this provider configuration?')) return;

    try {
      const result = await deleteProviderConfigDocument(providerId);
      if (result.success) {
        alert('Provider configuration deleted successfully');
        fetchProviderConfigs();
        fetchStatistics();
      } else {
        alert(`Error deleting provider: ${result.error}`);
      }
    } catch (error) {
      console.error('Error deleting provider:', error);
      alert('Error deleting provider');
    }
  };

  // Test provider connection
  const handleTestConnection = async (providerId) => {
    setTestingProviderId(providerId);
    try {
      const result = await testProviderConnection(providerId);
      if (result.success) {
        alert('Connection test successful');
        fetchProviderConfigs();
      } else {
        alert(`Connection test failed: ${result.error}`);
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      alert('Error testing connection');
    } finally {
      setTestingProviderId(null);
    }
  };

  // Toggle provider status
  const handleToggleStatus = async (providerId, isActive) => {
    try {
      const result = await toggleProviderStatus(providerId, isActive, currentUser.uid);
      if (result.success) {
        alert(`Provider ${isActive ? 'activated' : 'deactivated'} successfully`);
        fetchProviderConfigs();
        fetchStatistics();
      } else {
        alert(`Error toggling status: ${result.error}`);
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      alert('Error toggling status');
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      providerName: '',
      providerCode: '',
      displayName: '',
      displayNameEn: '',
      apiConfig: {
        baseUrl: '',
        apiKey: '',
        secretKey: '',
        timeout: 30000,
        rateLimit: {
          requestsPerMinute: 60,
          requestsPerHour: 1000
        }
      },
      settings: {
        isActive: true,
        priority: 1,
        defaultLanguage: 'th',
        defaultCurrency: 'THB',
        supportedCountries: ['TH'],
        supportedPropertyTypes: ['hotels']
      },
      commissionConfig: {
        enabled: true,
        rates: {
          customer: 0.05,
          momo: 0.08,
          girl: 0.03,
          admin: 0.10
        },
        minimumCommission: 10,
        maximumCommission: 10000
      },
      features: {
        search: true,
        booking: true,
        cancellation: true,
        reviews: true,
        images: true,
        availability: true,
        pricing: true
      },
      status: 'active'
    });
  };

  // Open edit modal
  const openEditModal = (provider) => {
    setSelectedProvider(provider);
    setFormData({
      ...provider,
      apiConfig: provider.apiConfig || {
        baseUrl: '',
        apiKey: '',
        secretKey: '',
        timeout: 30000,
        rateLimit: {
          requestsPerMinute: 60,
          requestsPerHour: 1000
        }
      },
      settings: provider.settings || {
        isActive: true,
        priority: 1,
        defaultLanguage: 'th',
        defaultCurrency: 'THB',
        supportedCountries: ['TH'],
        supportedPropertyTypes: ['hotels']
      },
      commissionConfig: provider.commissionConfig || {
        enabled: true,
        rates: {
          customer: 0.05,
          momo: 0.08,
          girl: 0.03,
          admin: 0.10
        },
        minimumCommission: 10,
        maximumCommission: 10000
      },
      features: provider.features || {
        search: true,
        booking: true,
        cancellation: true,
        reviews: true,
        images: true,
        availability: true,
        pricing: true
      }
    });
    setShowEditModal(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="text-red-600 mb-4">
          <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Providers</h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={fetchProviderConfigs}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Provider Configuration Manager</h1>
        <p className="text-gray-600">Manage API providers and their configurations</p>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Providers</p>
                <p className="text-2xl font-semibold text-gray-900">{statistics.totalProviders}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Providers</p>
                <p className="text-2xl font-semibold text-gray-900">{statistics.activeProviders}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Requests</p>
                <p className="text-2xl font-semibold text-gray-900">{statistics.totalRequests.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Commission</p>
                <p className="text-2xl font-semibold text-gray-900">฿{statistics.totalCommission.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="mb-6 flex gap-4">
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <span>+</span>
          Add New Provider
        </button>
        
        <button
          onClick={handleInitializeProviders}
          disabled={initializing}
          className="bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          {initializing ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            <span>🚀</span>
          )}
          Initialize Predefined Providers
        </button>
      </div>

      {/* Filters */}
      <div className="mb-6 bg-white rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
          <button
            onClick={clearFilters}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Clear All
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              {providerConfigModel.statusOptions.map(status => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Property Type
            </label>
            <select
              value={filters.propertyType}
              onChange={(e) => handleFilterChange('propertyType', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="hotels">Hotels</option>
              <option value="restaurants">Restaurants</option>
              <option value="attractions">Attractions</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <input
              type="text"
              value={filters.searchTerm}
              onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Search providers..."
            />
          </div>
        </div>
      </div>

      {/* Providers Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Provider
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Priority
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Statistics
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredProviders.map((provider) => {
              const formattedProvider = formatProviderConfigForDisplay(provider);
              return (
                <tr key={provider.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{provider.displayName}</div>
                      <div className="text-sm text-gray-500">{provider.providerCode}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${formattedProvider.statusColor}`}>
                        {formattedProvider.statusDisplay}
                      </span>
                      <div>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          provider.settings.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {provider.settings.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {provider.settings.priority}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div>
                      <div>Requests: {provider.stats.totalRequests}</div>
                      <div>Bookings: {provider.stats.totalBookings}</div>
                      <div>Commission: ฿{provider.stats.totalCommission.toLocaleString()}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEditModal(provider)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleTestConnection(provider.id)}
                        disabled={testingProviderId === provider.id}
                        className="text-green-600 hover:text-green-900 disabled:text-gray-400"
                      >
                        {testingProviderId === provider.id ? 'Testing...' : 'Test'}
                      </button>
                      <button
                        onClick={() => handleToggleStatus(provider.id, !provider.settings.isActive)}
                        className={`${provider.settings.isActive ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}`}
                      >
                        {provider.settings.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => handleDeleteProvider(provider.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {filteredProviders.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No providers found matching your criteria
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <ProviderConfigModal
          title="Create New Provider Configuration"
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleCreateProvider}
          onClose={() => {
            setShowCreateModal(false);
            resetForm();
          }}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && selectedProvider && (
        <ProviderConfigModal
          title="Edit Provider Configuration"
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleUpdateProvider}
          onClose={() => {
            setShowEditModal(false);
            setSelectedProvider(null);
            resetForm();
          }}
        />
      )}
    </div>
  );
};

// Provider Configuration Modal Component
const ProviderConfigModal = ({ title, formData, setFormData, onSubmit, onClose }) => {
  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      if (parent.includes('.')) {
        const [grandParent, parentKey] = parent.split('.');
        setFormData(prev => ({
          ...prev,
          [grandParent]: {
            ...prev[grandParent],
            [parentKey]: {
              ...prev[grandParent][parentKey],
              [child]: value
            }
          }
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: value
          }
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleArrayChange = (field, value) => {
    const array = value.split(',').map(item => item.trim()).filter(item => item);
    setFormData(prev => ({
      ...prev,
      [field]: array
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Provider Name
              </label>
              <input
                type="text"
                value={formData.providerName}
                onChange={(e) => handleInputChange('providerName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Provider Code
              </label>
              <input
                type="text"
                value={formData.providerCode}
                onChange={(e) => handleInputChange('providerCode', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Display Name (Thai)
              </label>
              <input
                type="text"
                value={formData.displayName}
                onChange={(e) => handleInputChange('displayName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Display Name (English)
              </label>
              <input
                type="text"
                value={formData.displayNameEn}
                onChange={(e) => handleInputChange('displayNameEn', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          {/* API Configuration */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">API Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Base URL
                </label>
                <input
                  type="url"
                  value={formData.apiConfig.baseUrl}
                  onChange={(e) => handleInputChange('apiConfig.baseUrl', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  API Key
                </label>
                <input
                  type="text"
                  value={formData.apiConfig.apiKey}
                  onChange={(e) => handleInputChange('apiConfig.apiKey', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Secret Key
                </label>
                <input
                  type="text"
                  value={formData.apiConfig.secretKey}
                  onChange={(e) => handleInputChange('apiConfig.secretKey', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Timeout (ms)
                </label>
                <input
                  type="number"
                  min="5000"
                  max="120000"
                  value={formData.apiConfig.timeout}
                  onChange={(e) => handleInputChange('apiConfig.timeout', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
            </div>
          </div>

          {/* Settings */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={formData.settings.priority}
                  onChange={(e) => handleInputChange('settings.priority', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Supported Countries (comma-separated)
                </label>
                <input
                  type="text"
                  value={formData.settings.supportedCountries.join(', ')}
                  onChange={(e) => handleArrayChange('settings.supportedCountries', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="TH, MY, SG, ID"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Supported Property Types (comma-separated)
                </label>
                <input
                  type="text"
                  value={formData.settings.supportedPropertyTypes.join(', ')}
                  onChange={(e) => handleArrayChange('settings.supportedPropertyTypes', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="hotels, restaurants, attractions"
                />
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.settings.isActive}
                  onChange={(e) => handleInputChange('settings.isActive', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                  Active
                </label>
              </div>
            </div>
          </div>

          {/* Commission Configuration */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Commission Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Customer Rate (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.commissionConfig.rates.customer * 100}
                  onChange={(e) => handleInputChange('commissionConfig.rates.customer', parseFloat(e.target.value) / 100)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Momo Rate (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.commissionConfig.rates.momo * 100}
                  onChange={(e) => handleInputChange('commissionConfig.rates.momo', parseFloat(e.target.value) / 100)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Girl Rate (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.commissionConfig.rates.girl * 100}
                  onChange={(e) => handleInputChange('commissionConfig.rates.girl', parseFloat(e.target.value) / 100)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Admin Rate (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.commissionConfig.rates.admin * 100}
                  onChange={(e) => handleInputChange('commissionConfig.rates.admin', parseFloat(e.target.value) / 100)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-4 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg"
            >
              Save Configuration
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProviderConfigManager;

